import type { Analytics } from "./analytics.js";
import { AUTO_EVENTS, PERFORMANCE_METRICS } from "mindbricks-analytics-shared";

interface AutoTrackFlags {
  clicks: boolean;
  forms: boolean;
  scroll: boolean;
  rageClick: boolean;
  deadClick: boolean;
  errors: boolean;
  performance: boolean;
}

const RAGE_WINDOW_MS = 1_000;
const RAGE_THRESHOLD = 3;
const DEAD_CLICK_OBSERVE_MS = 300;
const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;
const FORM_ABANDON_MS = 30_000;

/**
 * Lightweight auto-tracking. Strategy:
 *  - One document-level click listener feeds the canonical $click event,
 *    plus rage/dead click detection.
 *  - One focusin and one submit listener cover the form lifecycle.
 *  - Scroll depth uses a passive scroll listener guarded by rAF.
 *  - Errors hook window.onerror + unhandledrejection.
 *  - Performance uses PerformanceObserver for LCP/FCP/CLS/FID/TTFB/INP.
 */
export function attachAutoTracking(a: Analytics, flags: AutoTrackFlags): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};

  const detachers: Array<() => void> = [];

  if (flags.clicks || flags.rageClick || flags.deadClick) {
    detachers.push(attachClick(a, flags));
  }
  if (flags.forms) detachers.push(attachForms(a));
  if (flags.scroll) detachers.push(attachScroll(a));
  if (flags.errors) detachers.push(attachErrors(a));
  if (flags.performance) detachers.push(attachPerformance(a));

  return () => {
    for (const d of detachers) d();
  };
}

/* --------------------------- click → $click ------------------------------ */

interface ClickInfo {
  x: number;
  y: number;
  at: number;
  el: Element | null;
}

function attachClick(a: Analytics, flags: AutoTrackFlags): () => void {
  const recent: ClickInfo[] = [];

  // Shared MutationObserver for cheap dead-click detection. Counting
  // mutations is dramatically cheaper than reading outerHTML on every click.
  let mutationCount = 0;
  const mo = new MutationObserver((mutations) => {
    mutationCount += mutations.length;
  });
  mo.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true });

  const fireDeadClickCheck = (info: ClickInfo) => {
    const before = mutationCount;
    const beforeUrl = location.href;
    const beforeScroll = window.scrollY;
    setTimeout(() => {
      const noDom = mutationCount === before;
      const noNav = location.href === beforeUrl;
      const noScroll = window.scrollY === beforeScroll;
      if (noDom && noNav && noScroll && info.el && looksClickable(info.el)) {
        a.track(AUTO_EVENTS.DEAD_CLICK, describeClick(info.el));
      }
    }, DEAD_CLICK_OBSERVE_MS);
  };

  const onClick = (e: MouseEvent) => {
    const target = e.target instanceof Element ? e.target : null;
    const info: ClickInfo = { x: e.clientX, y: e.clientY, at: Date.now(), el: target };

    // Rage click — same general region (50px) within 1s.
    if (flags.rageClick && target) {
      recent.push(info);
      while (recent.length > 0 && info.at - (recent[0]?.at ?? 0) > RAGE_WINDOW_MS) recent.shift();
      const close = recent.filter((c) => Math.hypot(c.x - info.x, c.y - info.y) < 50);
      if (close.length >= RAGE_THRESHOLD) {
        a.track(AUTO_EVENTS.RAGE_CLICK, { count: close.length, ...describeClick(target) });
        recent.length = 0;
      }
    }

    // Canonical $click for any "interactive" target. We climb to the
    // closest interactive ancestor so a click on the icon inside a button
    // still attributes to the button.
    if (flags.clicks && target) {
      const interactive = target.closest(
        "[data-mb-id],[data-mb-component],[data-mb-action],[data-track],button,a,[role='button'],input[type='submit'],input[type='button']",
      );
      if (interactive) {
        a.track(AUTO_EVENTS.CLICK, { ...describeClick(interactive), x: e.clientX, y: e.clientY });
      }
    }

    if (flags.deadClick) fireDeadClickCheck(info);
  };

  document.addEventListener("click", onClick, true);
  return () => {
    document.removeEventListener("click", onClick, true);
    mo.disconnect();
  };
}

function looksClickable(el: Element): boolean {
  if (
    el.matches(
      "button, a, [role='button'], [data-track], [data-mb-id], [data-mb-action], input[type='submit'], input[type='button']",
    )
  )
    return true;
  try {
    if (getComputedStyle(el).cursor === "pointer") return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * Build the rich click payload. Adds:
 *   - type: button / link / tracked / element
 *   - tag/text/elementId/elementClass
 *   - href (for <a>)
 *   - action / componentId / componentType / generated  (Mindbricks metadata)
 *   - tracked + action  (data-track legacy)
 *
 * Honors `data-track-private` to suppress text capture.
 */
function describeClick(el: Element): Record<string, unknown> {
  const isPrivate = !!el.closest("[data-track-private]");
  const tag = el.tagName.toLowerCase();
  const text = isPrivate ? undefined : (el.textContent || "").trim().slice(0, 80) || undefined;

  let type: "button" | "link" | "tracked" | "element" = "element";
  if (tag === "button" || el.matches("[role='button'],input[type='submit'],input[type='button']")) type = "button";
  else if (tag === "a") type = "link";

  const dataTrackEl = el.closest("[data-track]");
  const dataTrack = dataTrackEl?.getAttribute("data-track") || undefined;
  if (dataTrack && type === "element") type = "tracked";

  const mbIdEl = el.closest("[data-mb-id]");
  const mbId = mbIdEl?.getAttribute("data-mb-id") || undefined;
  const mbComponent = el.closest("[data-mb-component]")?.getAttribute("data-mb-component") || undefined;
  const mbAction = el.closest("[data-mb-action]")?.getAttribute("data-mb-action") || undefined;

  const out: Record<string, unknown> = {
    type,
    tag,
    elementId: el.id || undefined,
    elementClass: el instanceof HTMLElement ? el.className.toString().slice(0, 120) : undefined,
  };
  if (text) out.text = text;
  if (tag === "a") {
    const href = el.getAttribute("href") || undefined;
    if (href) out.href = href;
  }
  if (dataTrack) {
    out.tracked = true;
    out.action = mbAction || dataTrack;
  }
  if (mbId) out.componentId = mbId;
  if (mbComponent) out.componentType = mbComponent;
  if (mbAction) out.action = mbAction;
  if (mbId || mbComponent || mbAction) out.generated = true;

  return out;
}

/* --------------------------------- forms --------------------------------- */

interface FormState {
  startedAt: number;
  fieldsTouched: Set<string>;
  submitted: boolean;
}

function describeForm(form: HTMLFormElement): Record<string, unknown> {
  const out: Record<string, unknown> = {
    tag: "form",
    elementId: form.id || undefined,
    name: form.name || undefined,
    action: form.action || undefined,
  };
  const mbId = form.closest("[data-mb-id]")?.getAttribute("data-mb-id") || undefined;
  const mbComponent = form.closest("[data-mb-component]")?.getAttribute("data-mb-component") || undefined;
  const mbAction = form.getAttribute("data-mb-action") || form.closest("[data-mb-action]")?.getAttribute("data-mb-action") || undefined;
  if (mbId) out.componentId = mbId;
  if (mbComponent) out.componentType = mbComponent;
  if (mbAction) out.action = mbAction;
  if (mbId || mbComponent || mbAction) out.generated = true;
  return out;
}

function attachForms(a: Analytics): () => void {
  const states = new WeakMap<HTMLFormElement, FormState>();
  const abandonTimers = new WeakMap<HTMLFormElement, ReturnType<typeof setTimeout>>();

  const armAbandon = (form: HTMLFormElement) => {
    const prev = abandonTimers.get(form);
    if (prev) clearTimeout(prev);
    abandonTimers.set(
      form,
      setTimeout(() => {
        const s = states.get(form);
        if (s && !s.submitted && s.fieldsTouched.size > 0) {
          a.track(AUTO_EVENTS.FORM_ABANDON, {
            ...describeForm(form),
            fields: s.fieldsTouched.size,
            durationMs: Date.now() - s.startedAt,
          });
        }
      }, FORM_ABANDON_MS),
    );
  };

  const onFocusIn = (e: FocusEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) {
      a.track(AUTO_EVENTS.INPUT_FOCUS, {
        name: t.name || undefined,
        type: (t as HTMLInputElement).type || t.tagName.toLowerCase(),
        ...(t.form ? { formId: t.form.id || undefined, formName: t.form.name || undefined } : {}),
      });

      const form = t.form;
      if (form) {
        let s = states.get(form);
        if (!s) {
          s = { startedAt: Date.now(), fieldsTouched: new Set(), submitted: false };
          states.set(form, s);
          a.track(AUTO_EVENTS.FORM_START, describeForm(form));
        }
        if (t.name) s.fieldsTouched.add(t.name);
        armAbandon(form);
      }
    }
  };

  const onSubmit = (e: SubmitEvent) => {
    const form = e.target as HTMLFormElement | null;
    if (!form || !(form instanceof HTMLFormElement)) return;
    const s = states.get(form);
    if (s) s.submitted = true;
    a.track(AUTO_EVENTS.FORM_SUBMIT, {
      ...describeForm(form),
      fields: s?.fieldsTouched.size ?? 0,
      durationMs: s ? Date.now() - s.startedAt : 0,
    });
    const t = abandonTimers.get(form);
    if (t) clearTimeout(t);
  };

  document.addEventListener("focusin", onFocusIn, true);
  document.addEventListener("submit", onSubmit, true);
  return () => {
    document.removeEventListener("focusin", onFocusIn, true);
    document.removeEventListener("submit", onSubmit, true);
  };
}

/* -------------------------------- scroll --------------------------------- */

function attachScroll(a: Analytics): () => void {
  const fired = new Set<number>();
  let raf = 0;

  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = Math.max(doc.scrollHeight, document.body.scrollHeight);
      if (total <= 0) return;
      const pct = Math.min(100, Math.round((scrolled / total) * 100));
      for (const t of SCROLL_THRESHOLDS) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          a.track(AUTO_EVENTS.SCROLL_DEPTH, { depth: t });
        }
      }
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  return () => {
    window.removeEventListener("scroll", onScroll);
    if (raf) cancelAnimationFrame(raf);
  };
}

/* -------------------------------- errors --------------------------------- */

function attachErrors(a: Analytics): () => void {
  const onError = (e: ErrorEvent) => {
    a.track(AUTO_EVENTS.ERROR, {
      message: e.message?.slice(0, 500),
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      stack: e.error?.stack?.slice(0, 2_000),
    });
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    a.track(AUTO_EVENTS.ERROR, {
      message: typeof reason === "string" ? reason.slice(0, 500) : (reason?.message ?? "unhandled_rejection"),
      stack: typeof reason?.stack === "string" ? reason.stack.slice(0, 2_000) : undefined,
      kind: "unhandled_rejection",
    });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

/* ----------------------------- web vitals ------------------------------- */

function attachPerformance(a: Analytics): () => void {
  if (typeof PerformanceObserver === "undefined") return () => {};
  const observers: PerformanceObserver[] = [];

  const safeObserve = (init: PerformanceObserverInit, cb: (entries: PerformanceEntryList) => void) => {
    try {
      const po = new PerformanceObserver((list) => cb(list.getEntries()));
      po.observe(init);
      observers.push(po);
    } catch {
      // entryType not supported in this browser
    }
  };

  // LCP — keep the most recent value; finalize on visibility hidden.
  let lastLcp = 0;
  safeObserve({ type: "largest-contentful-paint", buffered: true }, (entries) => {
    const last = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
    if (!last) return;
    lastLcp = last.renderTime ?? last.loadTime ?? last.startTime;
  });

  // FCP — fires once, report immediately.
  safeObserve({ type: "paint", buffered: true }, (entries) => {
    for (const e of entries) {
      if (e.name === "first-contentful-paint") {
        a.track(AUTO_EVENTS.PERFORMANCE, { metric: PERFORMANCE_METRICS.FCP, value: e.startTime });
      }
    }
  });

  // FID — first user-input delay; the spec emits exactly one entry.
  safeObserve({ type: "first-input", buffered: true }, (entries) => {
    const e = entries[0] as PerformanceEntry & { processingStart: number };
    if (!e) return;
    a.track(AUTO_EVENTS.PERFORMANCE, {
      metric: PERFORMANCE_METRICS.FID,
      value: Math.max(0, e.processingStart - e.startTime),
    });
  });

  // CLS — accumulated until tab hides.
  let cls = 0;
  safeObserve({ type: "layout-shift", buffered: true }, (entries) => {
    for (const e of entries as Array<PerformanceEntry & { value: number; hadRecentInput?: boolean }>) {
      if (!e.hadRecentInput) cls += e.value;
    }
  });

  // TTFB — single navigation entry.
  safeObserve({ type: "navigation", buffered: true }, (entries) => {
    const e = entries[0] as PerformanceNavigationTiming | undefined;
    if (!e) return;
    a.track(AUTO_EVENTS.PERFORMANCE, {
      metric: PERFORMANCE_METRICS.TTFB,
      value: Math.max(0, e.responseStart),
    });
  });

  // INP — slowest interaction by duration. Reported on hide alongside CLS/LCP.
  let inpMax = 0;
  safeObserve({ type: "event", durationThreshold: 16, buffered: true } as PerformanceObserverInit, (entries) => {
    for (const e of entries as Array<PerformanceEntry & { interactionId?: number; duration: number }>) {
      if (e.interactionId && e.duration > inpMax) inpMax = e.duration;
    }
  });

  // The "report on hide" finalizers. visibilitychange is the standard
  // moment to emit accumulated metrics; pagehide as a backup.
  let finalized = false;
  const finalize = () => {
    if (finalized) return;
    if (document.visibilityState !== "hidden") return;
    finalized = true;
    if (lastLcp > 0) a.track(AUTO_EVENTS.PERFORMANCE, { metric: PERFORMANCE_METRICS.LCP, value: lastLcp });
    if (cls > 0) a.track(AUTO_EVENTS.PERFORMANCE, { metric: PERFORMANCE_METRICS.CLS, value: cls });
    if (inpMax > 0) a.track(AUTO_EVENTS.PERFORMANCE, { metric: PERFORMANCE_METRICS.INP, value: inpMax });
  };
  document.addEventListener("visibilitychange", finalize);
  window.addEventListener("pagehide", finalize);

  return () => {
    for (const o of observers) o.disconnect();
    document.removeEventListener("visibilitychange", finalize);
    window.removeEventListener("pagehide", finalize);
  };
}
